import { log, PushProcessingNotification } from "@repo/helpers";
import { Button, ButtonTypes, Loading } from "@repo/ui";
import { useState } from "react";

import { useOnboardingSessionsApi } from "../../api/onboardingSessionsApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { Discipline } from "../summary/discipline";
import { Available } from "./available";
import { Enabled } from "./enabled";
import style from "./style.module.scss";
import { SegmentWizardProvider, useSegmentWizard } from "./useSegmentWizard";

export default function OnboardingStates() {
	return (
		<SegmentWizardProvider>
			<OnboardingStatesContent />
		</SegmentWizardProvider>
	);
}

function OnboardingStatesContent() {
	const { repId, rep } = useRepContext();
	const { possibleStatesQuery, onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const { useRepSegmentsQuery, addRepresentativeSegment } = useRepSegmentsApi(repId);
	const { data, loading, clearQuery: clearRepSegmentsQuery } = useRepSegmentsQuery();
	const { openSegmentWizard } = useSegmentWizard();
	const [enablingAll, setEnablingAll] = useState(false);

	const handleEnableAll = async () => {
		const statesToEnable = possibleStatesQuery.data;
		if (!statesToEnable || statesToEnable.length === 0) return;

		const toast = PushProcessingNotification("Enabling all states...");
		setEnablingAll(true);
		try {
			const newIds: number[] = [];
			for (const state of statesToEnable) {
				const newId = await addRepresentativeSegment(state.stateId);
				newIds.push(newId);
			}

			await possibleStatesQuery.clearQuery();
			await clearRepSegmentsQuery();
			await onboardingRequirementsQuery.clearQuery();

			toast.success("Enabled all states!");
			openSegmentWizard(newIds, "States");
		} catch (e) {
			log(e, "error");
			toast.error("Failed to enable all states");
		} finally {
			setEnablingAll(false);
		}
	};

	const hasAvailableStates = possibleStatesQuery.data?.length > 1;

	return (
		<div className={style.container}>
			<div className={style.flexBetween}>
				<h2 className={style.heading}>Onboarding States</h2>
				{hasAvailableStates && (
					<Button
						types={[ButtonTypes.Outline]}
						onClick={handleEnableAll}
						loading={enablingAll}
						disabled={enablingAll}
					>
						Enable All
					</Button>
				)}
			</div>
			<Discipline lastDisciplineName={rep.lastDisciplineName} possibleBlacklist={rep.possibleBlacklist} />
			<Loading loading={loading} message="Loading Onboarding States...">
				<div className={style.spaceY}>
					{data?.map((segment) => (
						<Enabled
							key={`enabled-${segment.stateId}`}
							id={segment.id}
							title={segment.name}
							status={segment.statusName}
						/>
					))}
					{possibleStatesQuery.data?.map((state) => (
						<Available key={`disabled-${state.stateId}`} stateId={state.stateId} />
					))}
				</div>
			</Loading>
		</div>
	);
}
